import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import Map from "../../ProProfileShared/Map"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ContactSection = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const showContactForm = settings.showContactForm !== false
  const showPhoneNumber = settings.showPhoneNumber !== false
  const showEmail = settings.showEmail !== false
  const showAddress = settings.showAddress !== false
  const showBusinessHours = settings.showBusinessHours !== false
  const showSocialLinks = settings.showSocialLinks || false
  const showMap = settings.showMap || false
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor || "#ffffff"
  const formButtonText = settings.formButtonText || "Send Message"
  const phoneNumber = settings.phoneNumber || practice?.phoneNumber || ""
  const email = settings.email || practice?.email || ""
  const address = settings.address || practice?.fullAddress || ""
  // Generate readable business hours from location availableTimes
  const generateBusinessHoursFromLocations = () => {
    if (!practice?.locations || practice.locations.length === 0) {
      return ""
    }

    // Get all physical locations (office, home) with availableTimes
    const physicalLocations = practice.locations.filter(
      (location) =>
        (location.kind === "office" || location.kind === "home" || location.kind === "physical") &&
        location.availableTimes
    )

    if (physicalLocations.length === 0) {
      return ""
    }

    // Use the first location's hours as the primary hours
    const primaryLocation = physicalLocations[0]
    const availableTimes = primaryLocation.availableTimes

    if (!availableTimes || Object.keys(availableTimes).length === 0) {
      return ""
    }

    // Generate readable hours
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
    const hoursArray = []

    days.forEach((day) => {
      const dayData = availableTimes[day]
      if (dayData && dayData.enabled && dayData.blocks && dayData.blocks.length > 0) {
        const block = dayData.blocks[0] // Use first block
        if (block.starts_at && block.ends_at) {
          const dayHours = `${day}: ${block.starts_at} - ${block.ends_at}`
          hoursArray.push(dayHours)
        }
      }
    })

    return hoursArray.length > 0 ? hoursArray.join(", ") : ""
  }

  // Get business hours from settings, practice businessHours, or generate from locations
  const getBusinessHours = () => {
    // First priority: custom business hours set in the editor
    if (settings.businessHours && settings.businessHours.trim() !== "") {
      return settings.businessHours
    }

    // Second priority: practice business hours
    if (practice?.businessHours && practice.businessHours.trim() !== "") {
      return practice.businessHours
    }

    // Third priority: generate from location availableTimes
    const locationHours = generateBusinessHoursFromLocations()
    if (locationHours && locationHours.trim() !== "") {
      return locationHours
    }

    // Fourth priority: try to generate from booking availability
    if (practice?.bookingAvailability?.availableSlots) {
      const slots = practice.bookingAvailability.availableSlots
      if (slots.length > 0) {
        return "Hours vary - please check our booking calendar for available times"
      }
    }

    return ""
  }

  const businessHours = getBusinessHours()

  const handleFormSubmit = (e) => {
    e.preventDefault()
    // Handle form submission
    console.log("Contact form submitted") // eslint-disable-line no-console
  }

  const handlePhoneClick = () => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`
    }
  }

  const handleEmailClick = () => {
    if (email) {
      window.location.href = `mailto:${email}`
    }
  }

  const renderContactInfo = () => {
    const contactItems = []

    if (showPhoneNumber && phoneNumber) {
      contactItems.push(
        <div key="phone" className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full p-4" style={{ backgroundColor: theme?.colors?.accent || "#0891b2" }}>
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
            </div>
          </div>
          <h3
            className="mb-2 font-semibold"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            Phone
          </h3>
          <button
            onClick={handlePhoneClick}
            className="rounded-lg px-4 py-2 transition-all hover:shadow-md"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#1F2937",
              backgroundColor: theme?.colors?.background || "#ffffff",
              border: `1px solid ${theme?.colors?.accent || "#0891b2"}`
            }}>
            {phoneNumber}
          </button>
        </div>
      )
    }

    if (showEmail && email) {
      contactItems.push(
        <div key="email" className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full p-4" style={{ backgroundColor: theme?.colors?.accent || "#0891b2" }}>
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
          </div>
          <h3
            className="mb-2 font-semibold"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            Email
          </h3>
          <button
            onClick={handleEmailClick}
            className="rounded-lg px-4 py-2 transition-all hover:shadow-md"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#1F2937",
              backgroundColor: theme?.colors?.background || "#ffffff",
              border: `1px solid ${theme?.colors?.accent || "#0891b2"}`
            }}>
            {email}
          </button>
        </div>
      )
    }

    if (showAddress && address) {
      contactItems.push(
        <div key="address" className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full p-4" style={{ backgroundColor: theme?.colors?.accent || "#0891b2" }}>
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <h3
            className="mb-2 font-semibold"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            Address
          </h3>
          <p
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            {address}
          </p>
        </div>
      )
    }

    if (showBusinessHours && businessHours) {
      contactItems.push(
        <div key="hours" className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full p-4" style={{ backgroundColor: theme?.colors?.accent || "#0891b2" }}>
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <h3
            className="mb-2 font-semibold"
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            Hours
          </h3>
          <p
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: theme?.colors?.textDark || "#1F2937"
            }}>
            {businessHours}
          </p>
        </div>
      )
    }

    return contactItems
  }

  const renderContactForm = () => {
    if (!showContactForm) return null

    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        <h3
          className="mb-6 text-center text-xl font-semibold"
          style={{
            fontFamily: theme?.fonts?.heading || "serif",
            color: theme?.colors?.textDark || "#1F2937"
          }}>
          Send us a message
        </h3>

        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <input
              type="text"
              placeholder="First Name"
              className="rounded-lg border px-4 py-3 focus:outline-none focus:ring-2"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                borderColor: theme?.colors?.accent || "#0891b2",
                focusBorderColor: theme?.colors?.primary || theme?.colors?.accent || "#0891b2",
                focusRingColor: theme?.colors?.primary || theme?.colors?.accent || "#0891b2"
              }}
            />
            <input
              type="text"
              placeholder="Last Name"
              className="rounded-lg border px-4 py-3 focus:outline-none focus:ring-2"
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                borderColor: theme?.colors?.accent || "#0891b2",
                focusBorderColor: theme?.colors?.primary || theme?.colors?.accent || "#0891b2",
                focusRingColor: theme?.colors?.primary || theme?.colors?.accent || "#0891b2"
              }}
            />
          </div>

          <input
            type="email"
            placeholder="Email Address"
            className="w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              borderColor: theme?.colors?.accent || "#0891b2"
            }}
          />

          <input
            type="tel"
            placeholder="Phone Number"
            className="w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              borderColor: theme?.colors?.accent || "#0891b2"
            }}
          />

          <textarea
            placeholder="Message"
            rows={4}
            className="w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              borderColor: theme?.colors?.accent || "#0891b2"
            }}
          />

          <button
            type="submit"
            className="w-full rounded-lg px-6 py-3 font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
            style={{
              backgroundColor: theme?.colors?.primary || theme?.colors?.accent || "#0891b2",
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              border: `1px solid ${theme?.colors?.primary || theme?.colors?.accent || "#0891b2"}`
            }}>
            {formButtonText}
          </button>
        </form>
      </div>
    )
  }

  const contactItems = renderContactInfo()

  return (
    <section
      id="contact"
      className={twMerge("px-4 py-20", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: showBackgroundColor ? backgroundColor : theme?.colors?.secondary || "#F3F4F6" }}>
      <AnimatedSection className="mx-auto max-w-6xl">
        <div className="mb-12 space-y-4 text-center">
          {section.headline && (
            <h2
              className="text-3xl font-bold lg:text-4xl"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#1F2937"
              }}>
              {section.headline}
            </h2>
          )}
          {section.subheadline && (
            <p
              className="text-lg text-gray-600 lg:text-xl"
              style={{ fontFamily: theme?.fonts?.paragraph || "sans-serif" }}>
              {section.subheadline}
            </p>
          )}
          {section.body && (
            <>
              <style>{`
                .contact-section-content ul {
                  list-style-type: disc !important;
                  margin-left: 1.5rem !important;
                  margin-bottom: 1rem !important;
                }
                .contact-section-content li {
                  margin-bottom: 0.5rem !important;
                  display: list-item !important;
                }
                .contact-section-content ol {
                  list-style-type: decimal !important;
                  margin-left: 1.5rem !important;
                  margin-bottom: 1rem !important;
                }
                .contact-section-content p {
                  margin-bottom: 1rem !important;
                }
                .contact-section-content h1, .contact-section-content h2, .contact-section-content h3,
                .contact-section-content h4, .contact-section-content h5, .contact-section-content h6 {
                  margin-bottom: 0.5rem !important;
                  margin-top: 1rem !important;
                }
              `}</style>
              <div
                className="contact-section-content prose prose-lg mx-auto max-w-none text-base text-gray-600"
                style={{ fontFamily: theme?.fonts?.paragraph || "sans-serif" }}
                dangerouslySetInnerHTML={{ __html: section.body }}
              />
            </>
          )}
        </div>

        {showMap &&
          (() => {
            // Filter for physical locations - handle locations without kind or with empty kind
            const physicalLocations =
              practice?.locations?.filter((location) => {
                // If no kind is specified, assume it's a physical location if it has coordinates
                if (!location.kind || location.kind === "") {
                  return location.latitude && location.longitude
                }
                // Otherwise check for known physical location kinds
                return location.kind === "physical" || location.kind === "office" || location.kind === "home"
              }) || []

            return (
              <div className="mb-12">
                {physicalLocations.length > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200" style={{ height: "300px" }}>
                    <Map
                      practice={practice}
                      theme={{
                        colorAccent: theme?.colors?.accent || "#0891b2",
                        colorTextLight: "#ffffff",
                        colorBgLight: theme?.colors?.background || "#ffffff"
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-64 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
                    <div className="text-center">
                      <p className="mb-2 text-gray-500">No office location available</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })()}

        {contactItems.length > 0 && (
          <div
            className={twMerge(
              "mb-12 grid gap-8",
              contactItems.length === 1
                ? "mx-auto max-w-md grid-cols-1"
                : contactItems.length === 2
                ? "mx-auto max-w-2xl grid-cols-1 md:grid-cols-2"
                : contactItems.length === 3
                ? "grid-cols-1 md:grid-cols-3"
                : "grid-cols-1 lg:grid-cols-4 md:grid-cols-2"
            )}>
            {contactItems}
          </div>
        )}

        {showContactForm && <div className="mx-auto max-w-2xl">{renderContactForm()}</div>}

        {showSocialLinks && (
          <div className="mt-12 text-center">
            <h3
              className="mb-6 text-lg font-semibold"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                color: theme?.colors?.textDark || "#1F2937"
              }}>
              Follow Us
            </h3>
            <div className="flex justify-center space-x-4">
              {/* Social media icons would go here */}
              <div className="text-gray-400">Social media links can be configured here</div>
            </div>
          </div>
        )}
      </AnimatedSection>
    </section>
  )
}

export default ContactSection
